import React, { useContext, useEffect, useRef, useState } from 'react';

import { useCallbackWithErrorHandling } from 'app/ErrorHandling';
import { Segment } from 'appTypes';
import { nowUnix } from 'features/common/datetimeUtils';

import { MapContext } from '../MapContext';
import { MapSegmentTooltip } from '../tooltips/MapSegmentTooltip';
import {
  addOrUpdateMapHoveredSegment,
  getNearestSegmentFromMouseEvent,
  mapHasLayer,
  setHoverCursorTypeForHover,
} from '../utils/mapCommon';
import { WithExtras } from '../utils/mapUtils';
import {
  kMouseHoverTimeoutMillis,
  kSegmentLayerId,
} from '../utils/map_constants';

export function withSegmentTooltip<P extends WithExtras>(
  Component: React.ComponentType<P>,
): React.FC<P & { enableHover?: boolean; showTooltip?: boolean }> {
  type OuterProps = P & { enableHover?: boolean; showTooltip?: boolean };

  function WrappedComponent(props: OuterProps): React.ReactElement {
    const { enableHover = true, showTooltip = true, ...rest } = props;
    const { refMap, mapReady, mapStyle } = useContext(MapContext);

    const [tooltipSegment, setTooltipSegment] = useState<undefined | Segment>(
      undefined,
    );
    const refEnableHover = useRef<boolean>(enableHover);
    const refCurrentTooltipSegment = useRef<Segment | undefined>(
      tooltipSegment,
    );
    const refMouseoverTimeout = useRef(null);

    function showHoveredInfoForFeature(feature: Segment) {
      setTooltipSegment(feature);
      if (
        feature.properties.segment_id !==
        refCurrentTooltipSegment.current?.properties?.segment_id
      ) {
        addOrUpdateMapHoveredSegment(refMap.current, mapStyle, feature);
      }

      refCurrentTooltipSegment.current = feature;
      if (refMouseoverTimeout.current) {
        clearTimeout(refMouseoverTimeout.current.handle);
        refMouseoverTimeout.current = undefined;
      }
    }

    function hideHoveredInfo() {
      setTooltipSegment(undefined);
      refCurrentTooltipSegment.current = undefined;
      refMouseoverTimeout.current = undefined;
      if (refMap.current)
        addOrUpdateMapHoveredSegment(refMap.current, mapStyle, undefined);
    }

    const handleMouseLeaveEvent = useCallbackWithErrorHandling((event) => {
      if (!refMouseoverTimeout.current) {
        refMouseoverTimeout.current = {
          handle: setTimeout(hideHoveredInfo, kMouseHoverTimeoutMillis),
          set: nowUnix(),
        };
      } else if (
        nowUnix() - refMouseoverTimeout.current.set >
        kMouseHoverTimeoutMillis * 2
      ) {
        hideHoveredInfo();
      }
    });

    const handleMouseMoveEvent = useCallbackWithErrorHandling(
      (event: mapboxgl.MapMouseEvent) => {
        let nearestSegment: Segment;
        if (mapHasLayer(refMap.current, kSegmentLayerId)) {
          nearestSegment = getNearestSegmentFromMouseEvent(
            refMap.current,
            event,
            kSegmentLayerId,
          );
        }
        if (refEnableHover.current) {
          if (nearestSegment) {
            showHoveredInfoForFeature(nearestSegment);
          } else {
            handleMouseLeaveEvent(event);
          }
        }
        setHoverCursorTypeForHover(refMap.current, !!nearestSegment);
      },
    );

    useEffect(() => {
      if (!enableHover) hideHoveredInfo();
      refEnableHover.current = enableHover;
    }, [enableHover]);

    useEffect(() => {
      if (mapReady) {
        refMap.current.on('mousemove', handleMouseMoveEvent);
        refMap.current
          .getContainer()
          ?.addEventListener('mouseleave', hideHoveredInfo);
      }
      return () => {
        if (refMap.current) {
          refMap.current.off('mousemove', handleMouseMoveEvent);
          refMap.current
            .getContainer()
            ?.removeEventListener('mouseleave', hideHoveredInfo);
        }
      };
    }, [mapReady]);

    const { extras } = props;
    const mergedExtras = (
      <>
        {extras}
        {tooltipSegment && showTooltip && (
          <MapSegmentTooltip segment={tooltipSegment} />
        )}
      </>
    );

    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(rest as P)}
        extras={mergedExtras}
      />
    );
  }

  return WrappedComponent;
}
